import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BRANDING, PLANS } from "@/config/branding";
import { GoldButton, OutlineButton } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { ui } from "@/components/Primitives";
import { Label } from "@/components/ui/label";
import {
  Home, User, Users, Building2, Heart, UserPlus, Inbox, Check, Loader2, MapPin, Coins, ArrowRight, CreditCard, Lock,
} from "lucide-react";
import { toast } from "sonner";

const PROFILES = [
  { key: "Personal Finance", desc: "Everyday income, spending, savings & goals", icon: User },
  { key: "Investor", desc: "Properties, portfolio, insurance & tax-ready reports", icon: Building2 },
  { key: "Community Treasurer", desc: "Bookings, committee, reimbursements & reports", icon: Users },
];
const COUNTRIES = ["Australia", "New Zealand"];
const CURRENCIES = [["AUD", "A$"], ["NZD", "NZ$"], ["USD", "$"], ["EUR", "€"]];
const HOUSEHOLDS = [
  { key: "Single", icon: User }, { key: "Partner", icon: Heart },
  { key: "Family", icon: Users }, { key: "Community", icon: Building2 },
];
const INVITE_ROLES = ["Partner", "Accountant", "Committee Member", "Family Member"];
const TOTAL = 9;

const Tile = ({ active, onClick, icon: Icon, title, desc, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`text-left p-5 rounded-2xl border transition-all w-full ${
      active ? "border-[#C6A45C] bg-[#C6A45C]/10 shadow-[0_4px_18px_rgba(198,164,92,0.25)]"
             : "border-[#EAE0D2] bg-white/60 hover:border-[#D8BE83] hover:bg-white"}`}>
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-[#C6A45C] text-white" : "bg-[#F0E7D8] text-[#C6A45C]"}`}>
        {active ? <Check size={18} /> : <Icon size={18} strokeWidth={1.5} />}
      </div>
      <div>
        <div className="font-display text-lg text-[#2C2A28]">{title}</div>
        {desc && <div className="text-sm text-[#8A7F70] mt-1">{desc}</div>}
      </div>
    </div>
  </button>
);

export default function Onboarding() {
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [finished, setFinished] = useState(null);
  const [data, setData] = useState({
    plan: localStorage.getItem("finetomi_plan") || "personal",
    profiles: [], country: "Australia", currency: "AUD", household: "Single", invites: {},
  });

  const selectedPlan = PLANS.find((p) => p.key === data.plan) || PLANS[0];

  const toggleProfile = (k) =>
    setData((d) => ({ ...d, profiles: d.profiles.includes(k) ? d.profiles.filter((p) => p !== k) : [...d.profiles, k] }));

  const next = () => {
    if (step === 3 && data.profiles.length === 0) return toast.error("Choose at least one profile");
    setStep((s) => Math.min(TOTAL, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const finish = async () => {
    setSaving(true);
    try {
      const invites = Object.entries(data.invites)
        .filter(([, v]) => v && v.trim())
        .map(([role, email]) => `${role}:${email.trim()}`);
      const u = await completeOnboarding({
        plan: data.plan, profiles: data.profiles, country: data.country,
        currency: data.currency, household: data.household, invites,
      });
      localStorage.removeItem("finetomi_plan");
      setFinished(u);
      setStep(TOTAL);
    } catch {
      toast.error("Could not complete setup. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="font-display text-3xl gold-text tracking-wider">{BRANDING.name}</div>
          {!finished && (
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {Array.from({ length: TOTAL }).map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i + 1 <= step ? "w-7 bg-[#C6A45C]" : "w-3.5 bg-[#E4DACB]"}`} />
              ))}
            </div>
          )}
        </div>

        <div className="glass-card rounded-3xl p-8 md:p-10 fade-up" data-testid="onboarding-card">
          {/* Step 1 — Welcome */}
          {step === 1 && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#F0E7D8] flex items-center justify-center mx-auto mb-6">
                <Home size={28} className="text-[#C6A45C]" strokeWidth={1.4} />
              </div>
              <h1 className="font-display text-3xl font-light text-[#2C2A28] mb-3">{BRANDING.onboarding.welcomeTitle}</h1>
              <p className="text-[#8A7F70] max-w-md mx-auto">{BRANDING.onboarding.welcomeBody}</p>
              <div className="mt-8 flex justify-center">
                <GoldButton data-testid="onboarding-start" onClick={next}>Let's begin <ArrowRight size={16} /></GoldButton>
              </div>
            </div>
          )}

          {/* Step 2 — Plan */}
          {step === 2 && (
            <div>
              <h2 className="font-display text-2xl font-light mb-1">Confirm your plan</h2>
              <p className="text-[#8A7F70] text-sm mb-6">You can change this anytime — billing isn't charged yet.</p>
              <div className="space-y-3">
                {PLANS.map((p) => (
                  <button key={p.key} type="button" data-testid={`onboarding-plan-${p.key}`}
                    onClick={() => setData((d) => ({ ...d, plan: p.key }))}
                    className={`w-full text-left p-5 rounded-2xl border transition-all flex items-center justify-between ${
                      data.plan === p.key ? "border-[#C6A45C] bg-[#C6A45C]/10 shadow-[0_4px_18px_rgba(198,164,92,0.25)]"
                                          : "border-[#EAE0D2] bg-white/60 hover:border-[#D8BE83]"}`}>
                    <div>
                      <div className="font-display text-lg text-[#2C2A28]">{p.name}</div>
                      <div className="text-sm text-[#8A7F70]">{p.tagline}</div>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <span className="font-display text-2xl gold-text">${p.price}</span>
                      <span className="text-xs text-[#A99F90]">/mo</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3 — Profiles */}
          {step === 3 && (
            <div>
              <h2 className="font-display text-2xl font-light mb-1">How will you use {BRANDING.name}?</h2>
              <p className="text-[#8A7F70] text-sm mb-6">Choose one or more. This tailors your experience.</p>
              <div className="space-y-3">
                {PROFILES.map((p) => (
                  <Tile key={p.key} testid={`profile-${p.key.replace(/\s/g, "-").toLowerCase()}`}
                    active={data.profiles.includes(p.key)} onClick={() => toggleProfile(p.key)}
                    icon={p.icon} title={p.key} desc={p.desc} />
                ))}
              </div>
            </div>
          )}

          {/* Step 4 — Country */}
          {step === 4 && (
            <div>
              <h2 className="font-display text-2xl font-light mb-1 flex items-center gap-2"><MapPin size={20} className="text-[#C6A45C]" /> Where is home?</h2>
              <p className="text-[#8A7F70] text-sm mb-6">We'll tailor tax, timezone and currency defaults.</p>
              <div className="grid grid-cols-2 gap-3">
                {COUNTRIES.map((c) => (
                  <Tile key={c} testid={`country-${c.replace(/\s/g, "-").toLowerCase()}`}
                    active={data.country === c} onClick={() => setData((d) => ({ ...d, country: c }))} icon={MapPin} title={c} />
                ))}
              </div>
            </div>
          )}

          {/* Step 5 — Currency */}
          {step === 5 && (
            <div>
              <h2 className="font-display text-2xl font-light mb-1 flex items-center gap-2"><Coins size={20} className="text-[#C6A45C]" /> Your currency</h2>
              <p className="text-[#8A7F70] text-sm mb-6">Change it later in Settings whenever you like.</p>
              <div className="grid grid-cols-2 gap-3">
                {CURRENCIES.map(([c, sym]) => (
                  <Tile key={c} testid={`currency-${c}`} active={data.currency === c}
                    onClick={() => setData((d) => ({ ...d, currency: c }))} icon={Coins} title={`${c} — ${sym}`} />
                ))}
              </div>
            </div>
          )}

          {/* Step 6 — Household */}
          {step === 6 && (
            <div>
              <h2 className="font-display text-2xl font-light mb-1">Your household</h2>
              <p className="text-[#8A7F70] text-sm mb-6">This helps us shape your home.</p>
              <div className="grid grid-cols-2 gap-3">
                {HOUSEHOLDS.map((h) => (
                  <Tile key={h.key} testid={`household-${h.key.toLowerCase()}`} active={data.household === h.key}
                    onClick={() => setData((d) => ({ ...d, household: h.key }))} icon={h.icon} title={h.key} />
                ))}
              </div>
            </div>
          )}

          {/* Step 7 — Invites */}
          {step === 7 && (
            <div>
              <h2 className="font-display text-2xl font-light mb-1 flex items-center gap-2"><UserPlus size={20} className="text-[#C6A45C]" /> Invite your circle</h2>
              <p className="text-[#8A7F70] text-sm mb-6">Optional — add their email to share access later.</p>
              <div className="space-y-3">
                {INVITE_ROLES.map((role) => (
                  <div key={role}>
                    <Label className={ui.label}>{role}</Label>
                    <Input data-testid={`invite-${role.replace(/\s/g, "-").toLowerCase()}`} type="email"
                      value={data.invites[role] || ""} placeholder="name@email.com (optional)"
                      onChange={(e) => setData((d) => ({ ...d, invites: { ...d.invites, [role]: e.target.value } }))}
                      className={ui.input} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 8 — Payment placeholder */}
          {step === 8 && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#F0E7D8] flex items-center justify-center mx-auto mb-6">
                <CreditCard size={28} className="text-[#C6A45C]" strokeWidth={1.4} />
              </div>
              <h2 className="font-display text-2xl font-light mb-2">Confirm & start your trial</h2>
              <div className="bg-white/70 border border-[#EAE0D2] rounded-2xl p-5 max-w-sm mx-auto my-6">
                <div className="flex items-center justify-between">
                  <span className="text-[#6b6256]">{selectedPlan.name}</span>
                  <span className="font-display text-2xl gold-text">${selectedPlan.price}<span className="text-xs text-[#A99F90]">/mo</span></span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 text-xs text-[#A99F90] mb-6">
                <Lock size={13} /> Secure payments via Stripe — coming soon. No card required during beta.
              </div>
              <div className="flex justify-center">
                <GoldButton data-testid="onboarding-payment-continue" onClick={finish} disabled={saving}>
                  {saving ? <Loader2 className="animate-spin" size={18} /> : <>Continue to setup <ArrowRight size={16} /></>}
                </GoldButton>
              </div>
            </div>
          )}

          {/* Step 9 — Done */}
          {step === 9 && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#F0E7D8] flex items-center justify-center mx-auto mb-6">
                {finished ? <Check size={28} className="text-[#8FB996]" /> : <Inbox size={28} className="text-[#C6A45C]" strokeWidth={1.4} />}
              </div>
              {finished ? (
                <>
                  <h2 className="font-display text-2xl font-light mb-2">{BRANDING.onboarding.finishTitle}</h2>
                  <p className="text-[#8A7F70] mb-4">Email your receipts &amp; statements to:</p>
                  <div data-testid="inbox-email" className="font-mono-data text-sm bg-white/70 border border-[#EAE0D2] rounded-xl px-4 py-3 inline-block gold-text">
                    {BRANDING.inboxEmail}
                  </div>
                  <p className="text-xs text-[#A99F90] mt-3 max-w-sm mx-auto">
                    Send from your account email and your documents appear in your Financial Inbox automatically — or upload them directly anytime.
                  </p>
                  <div className="mt-8 flex justify-center">
                    <GoldButton data-testid="onboarding-enter" onClick={() => navigate("/")}>Enter {BRANDING.name} <ArrowRight size={16} /></GoldButton>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3 py-6 text-[#8A7F70]">
                  <Loader2 className="animate-spin text-[#C6A45C]" size={28} /> Setting up your home…
                </div>
              )}
            </div>
          )}

          {/* Nav buttons (collection steps only) */}
          {step > 1 && step <= 7 && (
            <div className="flex justify-between mt-8">
              <OutlineButton data-testid="onboarding-back" onClick={back}>Back</OutlineButton>
              <GoldButton data-testid="onboarding-next" onClick={next}>Continue <ArrowRight size={16} /></GoldButton>
            </div>
          )}
          {step === 8 && (
            <div className="flex justify-center mt-6">
              <OutlineButton data-testid="onboarding-back" onClick={back}>Back</OutlineButton>
            </div>
          )}
        </div>
        {!finished && (
          <div className="text-center mt-4 text-xs text-[#A99F90]">Signed in as {user?.email}</div>
        )}
      </div>
    </div>
  );
}
